import {
  CLAIM_REJECTED_BANNER,
  VOICE_STATUS_MESSAGES,
  ballAnnouncement,
  gameFinishedAnnouncement,
  roundStartAnnouncement,
  winnerAnnouncement,
  remainingBalls,
  type EventState,
  type ServerMessage,
  type WinnerState,
} from '@bingo/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Banner, ClaimCardOverlay } from '../../components/ClaimOverlay.js';
import { Celebration } from '../../components/Celebration.js';
import { Stage } from '../../components/Stage.js';
import { SummaryOverlay } from '../../components/SummaryOverlay.js';
import {
  advanceMark,
  markForRound,
  shouldNarrateBall,
  type NarrationMark,
} from '../../lib/ballNarration.js';
import { currentRound, reduceEventState } from '../../lib/eventState.js';
import { sfx } from '../../lib/sfx.js';
import { shouldSpeak } from '../../lib/narration.js';
import { cancelSpeech, dropQueuedSpeech, speak, type SpeechKind } from '../../lib/speech.js';
import { useRevealedRound } from '../../lib/useRevealedRound.js';
import {
  currentNativeVoice,
  refreshVoices,
  selectVoice,
  unlockFromGesture,
  useVoiceState,
} from '../../lib/voiceStore.js';
import { connectRealtime, type ConnectionStatus } from '../../lib/ws.js';
import { Pairing } from './Pairing.js';
import { useDisplaySession } from './useDisplaySession.js';

/**
 * O TELÃO.
 *
 * Só exibe. Não existe um único botão de controle aqui — a única coisa
 * acionável é "Ativar som", e ela existe porque o navegador exige um gesto do
 * usuário antes de deixar qualquer áudio tocar.
 *
 * Tudo é navegável por controle remoto (setas + OK), porque o cenário alvo é
 * uma TV na sala de casa, sem mouse e sem teclado.
 */

type Overlay =
  | { kind: 'none' }
  | { kind: 'claim'; data: Extract<ServerMessage, { type: 'claim:result' }>['data'] }
  | { kind: 'claim-open'; cardNumber: string | null }
  | { kind: 'celebration'; winner: WinnerState }
  | { kind: 'banner'; title: string; subtitle: string }
  /**
   * Resumo de uma partida antiga, pedido pelo /historico.
   *
   * Fica até alguém mandar tirar — nada de timeout. O operador projetou isto
   * para a sala olhar e comentar; sumir sozinho no meio da conversa seria o
   * oposto do que ele pediu.
   */
  | {
      kind: 'history';
      summary: Extract<ServerMessage, { type: 'history:shown' }>['data']['summary'];
      eventName: string;
      startedAt: string;
    };

export function Telao() {
  const { phase, session, request, message, announce, invalidate } = useDisplaySession();

  const [state, setState] = useState<EventState | null>(null);
  const [status, setStatus] = useState<ConnectionStatus>('connecting');
  const [overlay, setOverlay] = useState<Overlay>({ kind: 'none' });

  /** Número provisório que roda na bola durante o giro. */
  const [spinBall, setSpinBall] = useState<number | null>(null);
  /** Animação de entrada da bola revelada. */
  const [revealAnimation, setRevealAnimation] = useState<'roll' | null>(null);
  /** Flash do "Repetir". */
  const [flashing, setFlashing] = useState(false);

  const voice = useVoiceState();

  const stateRef = useRef<EventState | null>(null);
  stateRef.current = state;

  const connectionRef = useRef<ReturnType<typeof connectRealtime> | null>(null);
  const overlayTimer = useRef<number | null>(null);

  const round = currentRound(state);

  /**
   * O que o público pode ver AGORA. Bola em suspense fica de fora de tudo —
   * painel, contador, últimas bolas — até o instante prometido pelo servidor.
   */
  const revealed = useRevealedRound(round);

  /** Rodada já filtrada: é esta que o palco desenha. */
  const visibleRound = useMemo(
    () => (round ? { ...round, balls: revealed.visibleBalls } : null),
    [round, revealed.visibleBalls],
  );

  const settings = state?.settings;

  /** Este aparelho, do ponto de vista da regra de narração. */
  const device = { kind: 'SYNTHESIS' as const, unlocked: voice.unlocked };

  const narrationHere = settings ? shouldSpeak(device, settings) : false;

  const say = useCallback(
    (text: string, options?: { force?: boolean; kind?: SpeechKind }) => {
      if (
        !settings ||
        !shouldSpeak({ kind: 'SYNTHESIS', unlocked: voice.unlocked }, settings, options)
      ) {
        return;
      }

      speak(text, {
        voice: currentNativeVoice(),
        rate: settings.rate,
        kind: options?.kind ?? 'ball',
      });
    },
    [settings, voice.unlocked],
  );

  const showOverlay = useCallback((next: Overlay, ms?: number) => {
    if (overlayTimer.current !== null) window.clearTimeout(overlayTimer.current);
    setOverlay(next);

    if (ms) {
      overlayTimer.current = window.setTimeout(() => setOverlay({ kind: 'none' }), ms);
    }
  }, []);

  /**
   * Giro da bola enquanto há bola em suspense.
   *
   * O número final JÁ está em `revealed.pending` — o giro é teatro puro, com
   * números aleatórios do globo. O que importa é que NADA que revele o número
   * de verdade mude antes de `revealAt`.
   */
  useEffect(() => {
    if (!revealed.pending) {
      setSpinBall(null);
      return;
    }

    const pool = remainingBalls(revealed.visibleBalls.map((ball) => ball.number));
    if (pool.length === 0) return;

    const timer = window.setInterval(() => {
      setSpinBall(pool[Math.floor(Math.random() * pool.length)] ?? null);
      if (stateRef.current?.settings.sfx && voice.unlocked) sfx.tick();
    }, 75);

    return () => window.clearInterval(timer);
    // `pending.ordinal` identifica a bola; desfazer durante o suspense muda
    // isto para `null` e o giro para sozinho.
  }, [revealed.pending?.ordinal, revealed.visibleBalls.length, voice.unlocked]);

  /**
   * Revelação: dispara quando uma bola NOVA passa a ser visível.
   *
   * Tudo acontece aqui, no mesmo instante — som, animação e narração — porque
   * o painel e o contador já mudaram junto, por derivarem da mesma lista.
   */
  const narrationMark = useRef<NarrationMark | null>(null);

  useEffect(() => {
    const last = revealed.lastRevealed;
    if (!last || !round) return;

    const nova = shouldNarrateBall(narrationMark.current, round.id, last);
    narrationMark.current = advanceMark(round.id, last);

    if (!nova) return;

    setSpinBall(null);
    setRevealAnimation('roll');
    // A animação de entrada dura ~0,85s; depois a bola fica parada.
    window.setTimeout(() => setRevealAnimation(null), 900);

    if (stateRef.current?.settings.sfx && voice.unlocked) sfx.reveal();

    say(ballAnnouncement(last.number, { digits: stateRef.current?.settings.digits ?? true }));
    // `round.id` entra nas dependências: sem ele, a primeira bola de uma
    // rodada nova com o MESMO ordinal da anterior não reexecutaria o efeito.
  }, [round?.id, revealed.lastRevealed?.ordinal, say, voice.unlocked]);

  const handleMessage = useCallback(
    (incoming: ServerMessage) => {
      setState((previous) => reduceEventState(previous, incoming));

      switch (incoming.type) {
        case 'state:sync':
          // Reconexão: o estado visível vem do relógio (`useRevealedRound`),
          // então uma bola ainda em suspense continua escondida e o giro
          // recomeça pelo tempo que falta. Nada a fazer aqui.
          setRevealAnimation(null);
          break;

        case 'ball:drawn':
          // O giro e a revelação são conduzidos por `revealAt`, não por este
          // evento: é o que mantém telão e controle no mesmo instante.
          break;

        case 'ball:undone':
          // Desfazer durante o suspense: a bola some da lista e o giro para.
          setSpinBall(null);
          setRevealAnimation(null);
          break;

        case 'round:started':
          setSpinBall(null);
          setRevealAnimation(null);
          // Marcador da RODADA NOVA: painel zerado vira `ordinal: 0`, e é
          // por isso que a primeira bola seguinte conta como nova. Antes
          // virava `null`, que o efeito lia como "primeira carga" e engolia
          // a bola em silêncio.
          narrationMark.current = markForRound(incoming.data.round);
          // A bola que esperava era do painel ANTERIOR: cantá-la agora
          // anunciaria um número que já saiu da tela.
          dropQueuedSpeech();
          showOverlay({ kind: 'none' });
          say(
            roundStartAnnouncement({
              roundNumber: incoming.data.round.number,
              modality: incoming.data.round.modality,
              keptBalls: incoming.data.keptBalls,
            }),
            { kind: 'announcement' },
          );
          break;

        case 'claim:opened':
          // A plateia vê a conferência acontecendo — senão o bingo "é
          // confirmado nos bastidores" e a casa perde a confiança do salão.
          showOverlay({ kind: 'claim-open', cardNumber: incoming.data.cardNumber });
          break;

        case 'claim:result': {
          if (incoming.data.status === 'CANCELADA') {
            showOverlay({ kind: 'none' });
            break;
          }

          showOverlay({ kind: 'claim', data: incoming.data }, 4000);

          if (incoming.data.status === 'NAO') {
            if (stateRef.current?.settings.sfx && voice.unlocked) sfx.rejected();
            window.setTimeout(
              () =>
                showOverlay(
                  {
                    kind: 'banner',
                    title: CLAIM_REJECTED_BANNER.title,
                    subtitle: CLAIM_REJECTED_BANNER.subtitle,
                  },
                  2800,
                ),
              4000,
            );
          }
          break;
        }

        case 'winner:confirmed':
          window.setTimeout(() => {
            showOverlay({ kind: 'celebration', winner: incoming.data.winner });
            if (stateRef.current?.settings.sfx && voice.unlocked) sfx.fanfare();
            say(winnerAnnouncement(incoming.data.winner.name), { kind: 'announcement' });
          }, 1200);
          break;

        case 'game:finished':
          showOverlay({ kind: 'none' });
          say(gameFinishedAnnouncement(incoming.data.gameNumber), { kind: 'announcement' });
          break;

        case 'history:shown':
          // Consulta projetada na TV: não muda o jogo, não narra nada. Se
          // falasse, o telão anunciaria o fim de uma partida que terminou
          // semana passada.
          showOverlay({
            kind: 'history',
            summary: incoming.data.summary,
            eventName: incoming.data.eventName,
            startedAt: incoming.data.startedAt,
          });
          break;

        case 'history:hidden':
          showOverlay({ kind: 'none' });
          break;

        case 'ball:repeat':
          // Flash na bola grande, como no protótipo, e fala mesmo com a
          // narração automática desligada.
          setRevealAnimation(null);
          setFlashing(true);
          window.setTimeout(() => setFlashing(false), 950);

          say(
            ballAnnouncement(incoming.data.number, {
              digits: stateRef.current?.settings.digits ?? true,
            }),
            { force: true },
          );
          break;

        case 'voice:test':
          // "Testar voz" com fonte Telão: quem fala é esta tela.
          if (voice.unlocked) {
            speak(incoming.data.text, {
              voice: currentNativeVoice(),
              rate: stateRef.current?.settings.rate ?? 0.95,
            });
          }
          break;

        case 'display:revoked':
          invalidate('Este telão foi desconectado.');
          break;

        default:
          break;
      }
    },
    [invalidate, say, showOverlay, voice.unlocked],
  );

  /**
   * O handler mais recente, sem fazer parte das dependências da conexão.
   *
   * `handleMessage` muda de identidade sempre que `settings` muda — e
   * `settings` vem de dentro do `state`, que o próprio WebSocket atualiza. Se
   * a conexão dependesse do handler, cada mensagem recebida derrubaria o
   * socket, o servidor mandaria `state:sync` ao reconectar, isso trocaria o
   * handler de novo, e o telão passaria o evento inteiro reconectando em
   * laço (foi exatamente o que aconteceu: 272 conexões em 6 segundos, até o
   * rate limit cortar o telão). A conexão só pode depender de **quem somos**
   * (o token), nunca do que estamos exibindo.
   */
  const handleMessageRef = useRef(handleMessage);
  handleMessageRef.current = handleMessage;

  // Conexão de tempo real, ligada ao token do telão — e só a ele.
  const displayToken = session?.displayToken ?? null;

  useEffect(() => {
    if (phase !== 'paired' || !displayToken) return;

    const connection = connectRealtime({
      query: { dt: displayToken },
      handlers: {
        onMessage: (incoming) => handleMessageRef.current(incoming),
        onStatus: setStatus,
      },
    });

    connectionRef.current = connection;

    return () => {
      connection.close();
      connectionRef.current = null;
    };
  }, [phase, displayToken]);

  /**
   * Informa ao servidor o estado do áudio.
   *
   * Três situações distintas (bloqueado / sem voz pt-BR / pronto) porque cada
   * uma pede uma ação diferente de quem opera — e ele precisa saber disso sem
   * levantar e ir até a TV. A lista de vozes vai junto para o /controle poder
   * escolher qual delas usar.
   */
  useEffect(() => {
    if (phase !== 'paired' || status !== 'open') return;

    connectionRef.current?.send({
      type: 'display:status',
      data: {
        status: voice.status,
        source: settings?.voiceSource ?? 'SYNTHESIS',
        voiceURI: voice.selectedVoiceURI,
        voices: voice.voices,
      },
    });
  }, [phase, status, voice.status, voice.selectedVoiceURI, voice.voices, settings?.voiceSource]);

  /** Carrega as vozes já no boot: a lista costuma demorar a chegar. */
  useEffect(() => {
    void refreshVoices(settings?.voice || null);
  }, [settings?.voice]);

  /** Troca de voz feita no /controle vale aqui na hora. */
  useEffect(() => {
    if (settings?.voice) selectVoice(settings.voice);
  }, [settings?.voice]);

  /** Narração desligada no meio de uma fala: cala na hora. */
  useEffect(() => {
    if (!narrationHere) cancelSpeech();
  }, [narrationHere]);

  // Mantém a tela acesa: o telão passa horas sem interação nenhuma.
  useEffect(() => {
    if (phase !== 'paired') return;
    let sentinel: WakeLockSentinel | null = null;

    const request = () => {
      void navigator.wakeLock
        ?.request('screen')
        .then((lock) => {
          sentinel = lock;
        })
        .catch(() => undefined);
    };

    request();
    document.addEventListener('visibilitychange', request);

    return () => {
      document.removeEventListener('visibilitychange', request);
      void sentinel?.release().catch(() => undefined);
    };
  }, [phase]);

  if (phase !== 'paired' || !session) {
    return <Pairing request={request} message={message} onRetry={() => void announce()} />;
  }

  if (!state) {
    return (
      <div className="pair tv">
        <h1>Carregando o telão…</h1>
        <p>{session.eventName}</p>
      </div>
    );
  }

  return (
    <div className="tv">
      <Stage
        state={state}
        /* Rodada FILTRADA: painel, contador, "Bola N" e as 3 últimas leem
           daqui, então mudam todos no mesmo instante da revelação. */
        round={visibleRound}
        bigBall={revealed.lastRevealed?.number ?? null}
        animation={revealed.pending ? 'shake' : flashing ? 'flash' : revealAnimation}
        suspenseBall={spinBall}
        headerExtra={
          !voice.unlocked ? (
            <div className="stat">
              <div className="k">Som</div>
              <div className="v">
                <span className="muted-chip" title={VOICE_STATUS_MESSAGES[voice.status]}>
                  🔇 mudo
                </span>
              </div>
            </div>
          ) : status !== 'open' ? (
            <div className="stat">
              <div className="k">Conexão</div>
              <div className="v">
                <span className="muted-chip">reconectando…</span>
              </div>
            </div>
          ) : null
        }
      />

      {!voice.unlocked ? (
        <div className="tv-claim" style={{ background: 'rgba(6,5,22,.78)' }}>
          <div className="tv-claim-inner">
            <h2>Ativar som do telão</h2>
            <p style={{ color: 'var(--muted)', fontSize: 'clamp(14px,2.4vh,28px)', margin: 0 }}>
              Aperte OK no controle remoto para a TV narrar as bolas.
            </p>
            <button
              type="button"
              className="tv-btn"
              autoFocus
              data-tv-focus
              onClick={() => {
                // SÍNCRONO, dentro do handler do clique: o navegador olha a
                // pilha de chamadas, e qualquer `await` antes disto já
                // descarta a permissão de áudio.
                unlockFromGesture();
              }}
            >
              🔊 Ativar som
            </button>
          </div>
        </div>
      ) : null}

      {overlay.kind === 'claim-open' ? (
        <div className="banner">
          <div>
            Conferindo cartela
            <small>{overlay.cardNumber ? `nº ${overlay.cardNumber}` : 'aguarde'}</small>
          </div>
        </div>
      ) : null}

      {overlay.kind === 'claim' && round ? (
        <ClaimCardOverlay
          title={overlay.data.title}
          detail={overlay.data.detail}
          {...(overlay.data.warning ? { warning: overlay.data.warning } : {})}
          cells={overlay.data.cells}
          drawn={round.balls.map((ball) => ball.number)}
          freeCenter={round.freeCenter}
          winningIndices={overlay.data.winningIndices}
          cardNumber={overlay.data.cells.length ? (round.pendingClaim?.cardNumber ?? null) : null}
        />
      ) : null}

      {overlay.kind === 'banner' ? (
        <Banner title={overlay.title} subtitle={overlay.subtitle} />
      ) : null}

      {overlay.kind === 'history' ? (
        <SummaryOverlay
          summary={overlay.summary}
          eventName={overlay.eventName}
          startedAt={overlay.startedAt}
          revisiting
        />
      ) : null}

      {overlay.kind === 'celebration' && round ? (
        <Celebration
          winner={overlay.winner}
          roundNumber={round.number}
          modality={round.modality}
          prizeText={round.prizeText}
        />
      ) : null}
    </div>
  );
}
